//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import moment from 'moment-timezone';
import { useState } from 'react';

import { getFeatureValue, setFeatureValue } from '@utils/featureStorage';
import { FeatureStorageScope } from '@utils/featureStorageModel';

type Units = 'days' | 'hours' | 'minutes' | 'seconds';

/**
 * Returns a boolean pertaining to whether or not the user requested to be reminded later and
 * a function to update that value.
 *
 * @param scope A FeatureStorageScope to uniquely identify the piece of UI being dismissed
 */
export const useRemindMeLater: (
  scope: FeatureStorageScope,
) => [boolean, (remindMeInDays: number, unitOverride?: Units) => void] = (
  scope,
) => {
  const [remindMeLaterRequested, setRemindMeLaterRequested] = useState(
    !!getFeatureValue<boolean>(scope),
  );

  // This function will store a value in localStorage using the featureStorage
  // util. The value we store `true` answers the question "has the current user
  // requested a delayed reminder for this scope?". While this value lives
  // in localStorage, the answer will be "yes" and `remindMeLaterRequested` will be true.
  // The value automatically expires on the `expires` date, at which point the answer
  // to the question will be "no" and `remindMeLaterRequested` will be false.
  const remindMeLater = (remindMeInDays: number, unitOverride?: Units) => {
    setRemindMeLaterRequested(true);
    setFeatureValue(scope, {
      value: true,
      expires: moment()
        .add(remindMeInDays, unitOverride ?? 'days')
        .toDate(),
    });
  };

  return [remindMeLaterRequested, remindMeLater];
};
